import { onError } from '@apollo/client/link/error'
import { captureException, withScope } from '@sentry/browser'
import { POSITION, TYPE, useToast } from 'vue-toastification'
import { NetworkError as NotificationError, Notification } from '@/utils/notification'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { debounce } from '@/utils/tools'
import enums from '@/utils/enum'
import BaseHtml from '@/components/BaseHtml.vue'

const errorQueue: string[] = [] // Queue to store error messages

// 401 unauthenticated, 419 expired session / CSRF token.
const SESSION_EXPIRED_STATUS_CODES = new Set([401, 419])

let sessionExpiredNotified = false

export const notifySessionExpired = (): void => {
  if (sessionExpiredNotified) return
  sessionExpiredNotified = true
  new Notification({
    msg: $t('Your session has expired. Please <a href="/login">log in</a> to continue.'),
    type: enums.notification.types.ERROR,
    severity: enums.notification.severity.CRITICAL,
    config: { timeout: false },
  }).dispatch()
}

// Function to generate a combined error message from the queue
const generateCombinedErrorMessage = (messages: string[]) => {
  if (messages.length === 0) return $t('An unknown error occurred.')

  return messages.pop()
}

// Debounced notification function
const debouncedNotification = debounce(() => {
  if (errorQueue.length > 0) {
    const combinedMessage = generateCombinedErrorMessage(errorQueue)
    errorQueue.length = 0 // Clear the queue
    new NotificationError({
      msg: combinedMessage,
      severity: enums.notification.severity.CRITICAL,
    }).dispatch()
  }
}, 300)

// Exported for unit testing.
export const handleApolloError: Parameters<typeof onError>[0] = ({
  graphQLErrors,
  networkError,
  operation,
}) => {
  const bypass = operation.getContext()?.skipGlobalErrorHandling ?? false
  // Add GraphQL errors to the queue
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      if (!bypass) {
        errorQueue.push($t('An unexpected error occurred while processing your request.'))
      }
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
      }
    })
  }

  // A non-JSON response (login or maintenance page) surfaces as ServerParseError.
  if (networkError && networkError.name === 'ServerParseError') {
    const { statusCode, bodyText } = networkError as {
      statusCode?: number
      bodyText?: string
    }

    if (statusCode !== undefined && SESSION_EXPIRED_STATUS_CODES.has(statusCode)) {
      // Session-expired notice supersedes any queued recoverable messages.
      errorQueue.length = 0
      notifySessionExpired()
    } else if (!bypass) {
      errorQueue.push(
        $t('Your session may have expired or the server is unavailable. Please refresh the page.'),
      )
    }
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error(`[ServerParseError]: status=${statusCode}`, '\nbody:', bodyText)
    }
  } else if (networkError) {
    // Add network error to the queue
    const networkMessage =
      networkError.name === 'ServerError'
        ? $t('A server error occurred. Please try again later.')
        : networkError.name === 'NetworkError'
          ? $t('A network error occurred. Please check your connection.')
          : $t('An unexpected network error occurred.')
    if (!bypass) {
      errorQueue.push(networkMessage)
    }
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error(`[Network error]: ${networkError}`)
    }
  }

  if (import.meta.env.DEV) {
    // Check for dd's and dumps in the error message
    const networkResult =
      networkError && 'result' in networkError && typeof networkError.result === 'string'
        ? networkError.result
        : null

    const dump = graphQLErrors?.find(({ extensions }, _) =>
      (extensions?.response as any)?.body?.startsWith(
        '<script> Sfdump = window.Sfdump || (function',
      ),
    )

    const dumpHtml = (dump?.extensions?.response as any)?.body ?? networkResult ?? false
    if (dumpHtml) {
      const toast = useToast()
      const content = {
        component: BaseHtml,
        props: {
          html: 'DD output <button>see dump</button>',
        },
        listeners: {
          click: () => {
            const win = window.open('', '', 'width=600,height=400')

            win?.document.write(`<!doctype html><html><head></head><body>${dumpHtml}</body></html>`)
            win?.document.close()
            win?.focus()
          },
        },
      }
      toast(content, {
        type: TYPE.INFO,
        position: POSITION.TOP_RIGHT,
        timeout: 10000,
        closeOnClick: true,
        pauseOnFocusLoss: true,
        pauseOnHover: true,
        showCloseButtonOnHover: true,
        hideProgressBar: true, // Has to be false to remove it
        closeButton: 'button',
        icon: true,
        rtl: false,
      })
    }
  }

  try {
    // Capture errors to Sentry with proper context
    if (graphQLErrors && graphQLErrors.length > 0) {
      graphQLErrors.forEach((error) => {
        withScope((scope) => {
          scope.setContext('graphql', {
            message: error.message,
            locations: error.locations,
            path: error.path,
            extensions: error.extensions,
          })
          scope.setTag('error_type', 'graphql')

          // Create a descriptive error object
          const sentryError = new Error(`GraphQL Error: ${error.message}`)
          sentryError.name = 'GraphQLError'
          captureException(sentryError)
        })
      })
    }

    if (networkError) {
      withScope((scope) => {
        // Extract error details safely
        const errorName = networkError.name
        const errorMessage = networkError.message
        const statusCode = 'statusCode' in networkError ? networkError.statusCode : undefined

        scope.setContext('network', {
          name: errorName,
          message: errorMessage,
          statusCode,
        })
        scope.setTag('error_type', 'network')

        // Create a descriptive error with the actual message
        const sentryError = new Error(`Network Error: ${errorMessage}`)
        sentryError.name = errorName
        captureException(sentryError)
      })
    }
  } catch (exception) {
    captureException(exception)
  }

  // Trigger debounced notification
  if (!bypass) {
    debouncedNotification()
  }
}

// Log any GraphQL errors or network errors that occurred
export const ErrorHandlingLink = onError(handleApolloError)
